import React, { memo } from 'react';
import classNames from 'classnames';

const TabItems = memo(
	({
		tabTitles,
		current,
		titleClassName,
		currentClassName,
		reverse,
		shiftTab,
		changeCurrent,
	}) => {
		const tabs = tabTitles.map(({ label, children, disabled }, index) => {
			const isSelected = current === index;

			return (
				<button
					className={`${titleClassName} ${
						(isSelected && currentClassName) || ''
					}`}
					role="tab"
					type="button"
					disabled={disabled}
					onClick={e => changeCurrent(e, index)}
					onKeyUp={shiftTab}
					aria-selected={isSelected}
					aria-label={label}
					key={label}
				>
					{children || label}
				</button>
			);
		});

		return reverse ? tabs.reverse() : tabs;
	}
);

class CTabs extends React.PureComponent {
	selectorStyle = {
		transform: 'scaleX(0)',
	};

	constructor(props) {
		super(props);

		this.$tabSelector = React.createRef();
		this.$tabWrapper = React.createRef();
		this.state = {
			mainClassname: 'c-tabs',
			titleClassName: 'c-tabs__tab',
		};
	}

	componentDidMount() {
		this.assignClassName(this.props);
		if (this.$tabSelector.current) {
			this.alignSelector();
			window.addEventListener('resize', this.alignSelector);
		}
	}

	componentDidUpdate(prevProps) {
		if (
			this.$tabSelector.current &&
			prevProps.updater !== this.props.updater
		) {
			this.alignSelector();
		}
	}

	componentWillUnmount() {
		if (this.$tabSelector.current) {
			window.removeEventListener('resize', this.alignSelector);
		}
	}

	assignClassName = ({
		className,
		fullWidth,
		type,
		titleClass,
		noSelector,
	}) => {
		this.setState({
			mainClassname: classNames(className, 'c-tabs', {
				'c-tabs--noSelector': noSelector,
				'c-tabs--capsule': type === 'capsule',
				'c-tabs--hollow': type === 'hollow',
			}),
			titleClassName: classNames(titleClass, 'c-tabs__tab', {
				'c-tabs__tab--fullWidth': fullWidth,
			}),
		});
	};

	setSelector = target => {
		const elLayoutPpops = target.getBoundingClientRect();

		this.$tabSelector.current.style.transform = `translateX(${
			target.offsetLeft
		}px)scaleX(${elLayoutPpops.width / 100})`;
	};

	changeCurrent = (e, index) => {
		const { dontBubble, noSelector, chooseCurrent } = this.props;

		if (e) {
			const { target } = e;

			if (dontBubble) {
				e.stopPropagation();
				e.nativeEvent.stopImmediatePropagation();
			}

			if (!noSelector && !!this.$tabSelector.current) {
				this.setSelector(target.closest('.c-tabs__tab'));
			}
		}
		chooseCurrent(parseInt(index, 10));
	};

	alignSelector = () => {
		requestAnimationFrame(() => {
			this.setSelector(
				this.$tabWrapper.current.querySelector('.current')
			);
		});
	};

	shiftTab = e => {
		const { chooseCurrent, current, tabTitles, noSelector } = this.props;
		const validNextElem =
			e.target.nextSibling &&
			e.target.nextSibling.classList.contains('c-tabs__tab');
		const nextIndex = validNextElem ? current + 1 : 0;
		const nextElem =
			(validNextElem && e.target.nextSibling) ||
			e.target.parentNode.querySelector('.c-tabs__tab');

		if (e.keyCode === 39) {
			nextElem.focus();
			if (!noSelector && !!this.$tabSelector.current) {
				this.setSelector(nextElem);
			}
			chooseCurrent(nextIndex);
		}

		const validPrevElem =
			e.target.previousSibling &&
			e.target.previousSibling.classList.contains('c-tabs__tab');
		const lastTabElem = tabTitles.length - 1;
		const prevIndex = validPrevElem ? current - 1 : lastTabElem;
		const prevElem =
			(validPrevElem && e.target.previousSibling) ||
			e.target.parentNode.querySelectorAll('.c-tabs__tab')[lastTabElem];

		if (e.keyCode === 37 || e.keyCode === 32) {
			prevElem.focus();
			if (!noSelector && !!this.$tabSelector.current) {
				this.setSelector(prevElem);
			}
			chooseCurrent(prevIndex);
		}
	};

	render() {
		const { tabTitles, titleClassCurrent, current, reverse, noSelector } =
			this.props;
		const { mainClassname, titleClassName } = this.state;
		const currentClassName = `current ${
			(titleClassCurrent && titleClassCurrent) || ''
		}`;

		return (
			<div role="tablist" className={mainClassname}>
				<div
					className={`c-tabs__tabWrapper ${
						(!noSelector && 'overflow-hidden') || ''
					} whitespace-nowrap`}
					ref={this.$tabWrapper}
				>
					{!noSelector && (
						<div
							className="c-tabs__tabSelector"
							style={this.selectorStyle}
							ref={this.$tabSelector}
						/>
					)}
					{tabTitles && tabTitles.length > 0 && (
						<TabItems
							tabTitles={tabTitles}
							current={current}
							titleClassName={titleClassName}
							currentClassName={currentClassName}
							reverse={reverse}
							shiftTab={this.shiftTab}
							changeCurrent={this.changeCurrent}
						/>
					)}
				</div>
			</div>
		);
	}
}

export default CTabs;
